import { useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useMemo } from 'react';
import {
  X,
  LayoutDashboard,
  TrendingUp,
  FileText,
  Users,
  Shield,
  Settings,
  LogOut,
  Rocket,
  CheckSquare,
} from 'lucide-react';
import { useAuth, useHasPermission, useFeature } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const baseNavItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/opportunities', label: 'Pipeline', icon: TrendingUp },
  { to: '/projects', label: 'Projects', icon: FileText },
  { to: '/customers', label: 'Customers', icon: Users },
];

const deliveryNavItem = { to: '/delivery', label: 'Delivery', icon: Rocket };
const reviewsNavItem = { to: '/reviews', label: 'Reviews', icon: CheckSquare };
const forecastNavItem = { to: '/forecast', label: 'Forecast', icon: TrendingUp };

const adminItems = [
  { to: '/admin', label: 'Admin', icon: Shield },
];

const userItems = [
  { to: '/settings', label: 'Settings', icon: Settings },
];

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const { user, logout, isLoggingOut } = useAuth();
  const hasAdminAccess = useHasPermission('admin.users.view');
  const showDelivery = useFeature('delivery_page');
  const showReviews = useFeature('review_workflow');
  const showForecast = useFeature('forecast_scenarios');

  const navItems = useMemo(() => {
    const items = [...baseNavItems];
    if (showDelivery) {
      items.splice(3, 0, deliveryNavItem);
    }
    if (showReviews) {
      const pipelineIdx = items.findIndex((i) => i.to === '/opportunities');
      items.splice(pipelineIdx + 1, 0, reviewsNavItem);
    }
    if (showForecast) {
      items.push(forecastNavItem);
    }
    return items;
  }, [showDelivery, showReviews, showForecast]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity md:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-xl transition-transform duration-300 ease-out md:hidden',
          'flex flex-col',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Header with close button */}
        <div className="flex h-16 items-center justify-between border-b border-gray-200 px-4">
          <Link to="/dashboard" className="flex items-center gap-2" onClick={onClose}>
            <img
              src="/xtravirt-logo.png"
              alt="Xtravirt"
              className="h-6"
            />
            <span className="font-heading text-xl font-bold text-xtravirt-dark">
              PST
            </span>
          </Link>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 transition-colors touch-target"
            aria-label="Close menu"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* User info */}
        <div className="border-b border-gray-200 px-4 py-4">
          <div className="flex items-center gap-3">
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name}
                className="h-12 w-12 rounded-full"
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-xtravirt-navy text-white text-lg font-medium">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">{user?.name}</p>
              <p className="text-sm text-gray-500 truncate">{user?.email}</p>
              {user?.isSuperAdmin && (
                <span className="mt-1 inline-block rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                  Super Admin
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4">
          <div className="space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors touch-target',
                    isActive
                      ? 'bg-xtravirt-cyan/10 text-xtravirt-navy'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-xtravirt-dark'
                  )
                }
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>

          {/* Admin section */}
          {hasAdminAccess && (
            <>
              <div className="my-4 border-t border-gray-200" />
              <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
                Administration
              </p>
              <div className="space-y-1">
                {adminItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={onClose}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors touch-target',
                        isActive
                          ? 'bg-xtravirt-cyan/10 text-xtravirt-navy'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-xtravirt-dark'
                      )
                    }
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </>
          )}

          {/* Settings */}
          <div className="my-4 border-t border-gray-200" />
          <div className="space-y-1">
            {userItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors touch-target',
                    isActive
                      ? 'bg-xtravirt-cyan/10 text-xtravirt-navy'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-xtravirt-dark'
                  )
                }
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            ))}

          </div>
        </nav>

        {/* Footer with logout */}
        <div className="border-t border-gray-200 p-4">
          <button
            onClick={() => {
              onClose();
              logout();
            }}
            disabled={isLoggingOut}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 touch-target"
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            <span>{isLoggingOut ? 'Signing out...' : 'Sign out'}</span>
          </button>

          {/* Version */}
          <p className="mt-4 px-4 text-xs text-gray-400">v1.0.0</p>
        </div>
      </div>
    </>
  );
}
